import { useEffect } from "react";

/**
 * Client-side updates <title>, <meta name=description>, and Open Graph tags
 * for a specific route. Note: search-engine and social-media scrapers see the
 * static tags from public/index.html — this only helps for in-app navigation
 * and browsers that re-read tags on route change. For real per-route SEO you'd
 * want SSR / prerendering.
 */
const upsertMeta = (selector, attr, value) => {
  if (!value) return;
  let el = document.head.querySelector(selector);
  if (!el) {
    el = document.createElement("meta");
    const [k, v] = selector.replace(/[[\]"]/g, "").split("=");
    el.setAttribute(k, v);
    document.head.appendChild(el);
  }
  el.setAttribute(attr, value);
};

const upsertLink = (rel, href) => {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

const upsertJsonLd = (id, data) => {
  const existing = document.getElementById(id);
  if (existing) existing.remove();
  if (!data) return;
  const el = document.createElement("script");
  el.type = "application/ld+json";
  el.id = id;
  el.text = JSON.stringify(data);
  document.head.appendChild(el);
};

export default function useMeta({ title, description, image, url, canonical, jsonLd }) {
  useEffect(() => {
    if (title) document.title = title;
    upsertMeta('meta[name="description"]', "content", description);
    upsertMeta('meta[property="og:title"]', "content", title);
    upsertMeta('meta[property="og:description"]', "content", description);
    upsertMeta('meta[property="og:image"]', "content", image);
    const finalUrl = canonical || url || (typeof window !== "undefined" ? window.location.href.split("?")[0] : "");
    upsertMeta('meta[property="og:url"]', "content", finalUrl);
    upsertMeta('meta[name="twitter:title"]', "content", title);
    upsertMeta('meta[name="twitter:description"]', "content", description);
    upsertMeta('meta[name="twitter:image"]', "content", image);
    upsertLink("canonical", finalUrl);
    if (jsonLd) upsertJsonLd("page-jsonld", jsonLd);
    return () => { const el = document.getElementById("page-jsonld"); if (el) el.remove(); };
  }, [title, description, image, url, canonical, jsonLd]);
}
